/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { createRoot } from "react-dom/client";
import { PopperContent } from "reactstrap";

// reactstrap bug: PopperContent.defaultProps.transition has no timeout, triggering a PropType warning.
if (PopperContent.defaultProps && !PopperContent.defaultProps.transition?.timeout) {
  PopperContent.defaultProps = {
    ...PopperContent.defaultProps,
    transition: { timeout: 150, appear: true, enter: true, exit: true, in: true },
  };
}
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PropTypes from "prop-types";

import "assets/plugins/nucleo/css/nucleo.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "assets/scss/argon-dashboard-react.scss";
import "assets/css/theme.css";
import "react-datetime/css/react-datetime.css";

import constants from "configs/constants";

import Standard from "layouts/Standard";
import ThingAuth from "layouts/ThingAuth";
import Admin from "layouts/Admin";

import IncidentBannerHost from "components/IncidentBanner/IncidentBannerHost";

import { withADB2CAuth } from "services/azureADB2C";

import { Provider, useDispatch, useSelector } from "react-redux";
import { initUserFromAuth } from "@redux/actions";
import store from "@redux/store";
import {
    AuthorizationContextProvider,
    useAuthorizationContext,
} from "contexts/AuthorizationContext";

import { ReactNotifications } from "react-notifications-component";
import "react-notifications-component/dist/theme.css";

import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import { initLanguage } from "translation";
import en_GB from "translation/English_en-GB.json";
import LoadFailed from "components/LoadFailed";
import { ThemeProvider } from "@/theme/ThemeContext";
import { TimeProvider } from "contexts/TimeContext.jsx";

import ExternalLink from "views/ExternalLink";
import { initAppConfigurationsAsync } from "@redux/reducers/appConfigurations";
import { applyScopedAppConfigurations } from "@redux/reducers/organizationAppConfigurations";
import { selectOrganizationFlags } from "@redux/selectors/appConfigurations";
import { initConfigurationAsync } from "@redux/reducers/configuration";

const ROOT_KEY = "__XploreAppRoot__";

// The preload screen can fail before initLanguage() has run: fall back to the
// bundled English rather than show a raw key.
const preloadText = (key) => {
    const fallback = key.split(".").reduce((node, part) => node?.[part], en_GB);
    return i18next.isInitialized
        ? i18next.t(key, { ns: "common", defaultValue: fallback })
        : fallback;
};

const waitForUiDelay = (ms) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

function RootApp(props) {
    const dispatch = useDispatch();
    const { auth } = props;
    const [isLanguageReady, setIsLanguageReady] = React.useState(false);
    const [isReady, setIsReady] = React.useState(false);
    const {
        isReady: isContextReady,
        activeOrganization,
        isSuperAdmin,
        initError,
        retryInitialize,
        isOrgFilterEnabled,
        organizationsInScope,
    } = useAuthorizationContext();
    // With the organization filter, the flags of the scope come from the
    // per-organization flags loaded at sign-in: no request when the filter
    // changes. A super admin's flags stay forced on, whatever the filter.
    const organizationFlags = useSelector(selectOrganizationFlags);
    const usesScopedFlags = isOrgFilterEnabled && !isSuperAdmin();
    const scopeGuids = organizationsInScope.map((org) => org.guid);
    const scopeKey = usesScopedFlags ? scopeGuids.join(",") : null;

    React.useEffect(() => {
        dispatch(initUserFromAuth(auth));
    }, [auth]);

    React.useEffect(() => {
        if (!isContextReady) return;

        let isMounted = true;

        const initializeApp = async () => {
            if (usesScopedFlags) {
                applyScopedAppConfigurations(dispatch, organizationFlags, scopeGuids);
            } else {
                // With the filter only a super admin gets here: their flags stay global.
                const flagsOrgGuid = isOrgFilterEnabled ? null : activeOrganization?.guid;
                await initAppConfigurationsAsync(dispatch, flagsOrgGuid, isSuperAdmin());
            }
            await initConfigurationAsync(dispatch);
            await initLanguage();
            if (!isMounted) return;

            setIsLanguageReady(true);
            await waitForUiDelay(100); // Delaying a bit to show the text from t("generic.loadingExperience")
            if (isMounted) {
                setIsReady(true);
            }
        };

        initializeApp();

        return () => {
            isMounted = false;
        };
        // The filter does not re-run the app init: the effect below swaps the flags.
    }, [dispatch, isContextReady, isOrgFilterEnabled ? null : activeOrganization, isSuperAdmin]);

    React.useEffect(() => {
        if (!isContextReady || !usesScopedFlags) return;
        applyScopedAppConfigurations(dispatch, organizationFlags, scopeGuids);
    }, [dispatch, isContextReady, usesScopedFlags, scopeKey, organizationFlags]);

    if (!isReady || !isContextReady) {
        return (
            <div
                style={{
                    display: "flex",
                    height: "100vh",
                    width: "100vw",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: "1rem",
                }}
            >
                {initError && !isContextReady ? (
                    // The sign-in load failed (customers listing, groups): say so,
                    // rather than keep the preload animation up forever.
                    <LoadFailed
                        message={preloadText("generic.loadFailed")}
                        retryLabel={preloadText("generic.retry")}
                        onRetry={retryInitialize}
                    />
                ) : (
                    constants.ICON_PRELOAD_EXPERIENCE
                )}
                {isLanguageReady && !(initError && !isContextReady) && (
                    <div className="text-body mt-9">
                        {i18next.t("generic.loadingExperience", { ns: "common" }) + " ..."}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <ReactNotifications />
            <TimeProvider>
                <BrowserRouter
                    future={{
                        v7_startTransition: true,
                        v7_relativeSplatPath: true,
                    }}
                >
                    <Routes>
                        <Route path="/std/*" element={<Standard {...props} />} />
                        <Route path="/ad/*" element={<Admin {...props} />} />
                        <Route path="/tr/*" element={<ThingAuth {...props} />} />
                        <Route path="/ext/*" element={<ExternalLink />} />
                        <Route path="/" element={<Navigate to="/std/index" replace />} />
                    </Routes>
                </BrowserRouter>
            </TimeProvider>
        </>
    );
}

// Use top-level await to bootstrap the app
const container =
    typeof document !== "undefined" ? document.getElementById("root") : null; // NOSONAR - This file is only used in a browser environment, so it's safe to check for document.
if (container) {
    let root = globalThis[ROOT_KEY];
    if (!root) {
        root = createRoot(container);
        globalThis[ROOT_KEY] = root;
    }

    const App = withADB2CAuth(RootApp);

    root.render(
        <Provider store={store}>
            <I18nextProvider i18n={i18next}>
                <ThemeProvider>
                    {/* Outside <App /> on purpose: App is wrapped in
                        withADB2CAuth, so anything inside it renders only after
                        authentication, and the banner must be visible before
                        login too. */}
                    <IncidentBannerHost />
                    <AuthorizationContextProvider>
                        <App />
                    </AuthorizationContextProvider>
                </ThemeProvider>
            </I18nextProvider>
        </Provider>,
    );

    if (import.meta.hot) {
        import.meta.hot.dispose(() => {
            try {
                root.unmount();
            } finally {
                delete globalThis[ROOT_KEY];
            }
        });
    }
}

// Explicit named export for HMR consistency
export { store };

RootApp.propTypes = {
    auth: PropTypes.object.isRequired,
};